import WorkThumbnail from '../../components/WorkThumbnail';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useWebSocket } from '../../context/WebSocketContext';
import { useUser } from '../../context/UserContext';
import Message from '../../components/Message';
import Masonry from 'react-masonry-css';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import ReactDOM from 'react-dom';

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background-color: ${({ theme }) => theme.background};
  position: relative;
  overflow: visible;

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      repeating-linear-gradient(
        45deg,
        ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'} 0px,
        ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'} 1px,
        transparent 1px,
        transparent 2px
      ),
      repeating-linear-gradient(
        -45deg,
        ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'} 0px,
        ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'} 1px,
        transparent 1px,
        transparent 2px
      ),
      linear-gradient(to bottom,
        ${({ theme }) => theme.cardBg},
        ${({ theme }) => theme.background}
      );
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
    opacity: ${({ theme }) => theme.isDark ? '0.08' : '0.12'};
    mix-blend-mode: ${({ theme }) => theme.isDark ? 'color-dodge' : 'multiply'};
    pointer-events: none;
    z-index: 0;
  }
`;

const Container = styled.div`
  padding: 20px 24px 20px 24px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  overflow: visible;

  @media (max-width: 1440px) {
    padding: 20px 24px;
  }

  @media (max-width: 1024px) {
    padding: 16px 20px;
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
  }
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;
  margin-bottom: ${props => props.$hasThreeImages ? '180px' : '60px'};
  align-items: flex-start;
  position: relative;
  padding-bottom: ${props => props.$hasThreeImages ? '0px' : '40px'};
  max-width: 100%;
  overflow: visible;

  @media (max-width: 1440px) {
    gap: 32px;
    margin-bottom: ${props => props.$hasThreeImages ? '160px' : '50px'};
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 24px;
    padding-bottom: 0;
    margin-bottom: 40px;
  }

  @media (max-width: 768px) {
    gap: 20px;
    margin-bottom: 32px;
  }

  @media (max-width: 480px) {
    gap: 16px;
    margin-bottom: 24px;
  }
`;

const MainContent = styled.div`
  flex: 0 0 auto;
  width: 30%;
  min-width: 0;

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ThemeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ThemeTitle = styled.h2`
  font-size: 18px;
  color: ${({ theme }) => theme.secondaryText};
  margin: 0;
  font-weight: 400;
`;

const ThemeContent = styled.h1`
  font-size: 28px;
  color: ${({ theme }) => theme.text};
  margin: 0;
  font-weight: 500;
  line-height: 1.3;

  @media (max-width: 1024px) {
    font-size: 24px;
  }

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const WinnerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WinnerTitle = styled.h2`
  font-size: 18px;
  color: ${({ theme }) => theme.secondaryText};
  margin: 0;
  font-weight: 400;
`;

const WinnerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const WinnerUser = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WinnerUserRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const WinnerAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  box-shadow: none;
`;

const WinnerName = styled.span`
  color: ${({ theme }) => theme.text};
  font-size: 16px;
  font-weight: 500;
`;

const VoteSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const VoteCount = styled.div`
  color: ${({ theme }) => theme.text};
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const VoterList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const VoterAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  border: none;
  box-shadow: none;
  cursor: pointer;
  transition: none;

  &:hover {
    transform: none;
  }
`;

const MoreVotersIndicator = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)'};
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  cursor: pointer;
  border: none;
  box-shadow: none;
  transition: none;
  user-select: none;
  font-weight: 500;

  &:hover {
    transform: none;
    background-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.15)'};
  }
`;

const ExtendedVoterList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const VoterPopup = styled.div`
  position: fixed;
  background: ${({ theme }) => theme.cardBg || 'white'};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 100;
  width: 220px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  left: ${props => props.$position?.x || 0}px;
  top: ${props => props.$position?.y || 0}px;
  transform: translateX(-50%);

  @media (max-width: 768px) {
    width: 200px;
  }
`;

const VoterPopupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const VoterPopupAvatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  box-shadow: none;
`;

const VoterPopupName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.text || '#000'};
`;

const WinnerImageSection = styled.div`
  flex: 1;
  overflow: visible;
  display: flex;
  justify-content: flex-end;
  max-width: calc(70% - 40px);
  padding-right: 0;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 100%;
    position: relative;
    left: auto;
    right: auto;
    top: auto;
  }
`;

const CreationImage = styled.img`
  width: 100%;
  max-width: 764px;
  aspect-ratio: 1;
  height: 100%;
  object-fit: cover;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.03)'};

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const WinnerImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${props => {
    switch(props.$totalImages) {
      case 1:
        return props.$isVertical ? '631px' : '430px';
      case 2:
        return '381px';
      case 3:
        return '373px';
      case 4:
        return '271.20416259765625px';
      case 5:
        return '211.76773071289062px';
      default:
        return '211.76773071289062px';
    }
  }};
  overflow: visible;
  aspect-ratio: ${props => {
    switch(props.$totalImages) {
      case 1:
        return props.$isVertical ? '3/4' : '16/9';
      case 2:
        return '3/4';
      case 3:
        return '371/373';
      case 4:
        return '269.75/271.20416259765625';
      case 5:
        return '3/4';
      default:
        return '3/4';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.isDarkMode ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.03)'};
  padding: ${props => props.$totalImages === 3 ? '0' : '0'};
  min-height: ${props => props.$totalImages === 3 ? 'auto' : 'auto'};
`;

const RankedImageItem = styled.div`
  break-inside: avoid;
  margin-bottom: 16px;
  transition: none;
  background: transparent;

  &:hover {
    transform: none;
  }

  @media (max-width: 768px) {
    &:hover {
      transform: none;
    }
  }
`;

const WinnerUserLabel = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WinnerUserAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

const CreationInfo = styled.div`
  padding: 16px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  position: relative;
  z-index: 2;
  overflow: visible;

  @media (max-width: 768px) {
    padding: 12px 0;
  }

  @media (max-width: 480px) {
    padding: 8px 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const CreationCard = styled.div`
  background: transparent;
  overflow: visible;
  transition: none;
  margin-bottom: 24px;
  width: 100%;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    margin-bottom: 20px;
  }

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    margin-bottom: 12px;
  }

  &:hover {
    transform: none;
  }
`;

const CreationCardImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.03)'};
  max-height: none;
`;

const CreationAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 0;
  position: relative;
  z-index: 2;
`;

const AuthorAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  box-shadow: none;
`;

const AuthorName = styled.span`
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : '#000000'};
  font-size: 14px;
  font-weight: 500;
`;

const CreationVotes = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 3;

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const VoterDropdown = styled.div`
  background: ${({ theme }) => theme.cardBg || 'white'};
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
  width: 240px;
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: contain; /* 防止滚动传播 */

  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
    }
  }

  @media (max-width: 1024px) {
    width: 220px;
  }

  @media (max-width: 768px) {
    width: 200px;
    max-width: calc(100vw - 32px); /* 确保不超出屏幕 */
  }

  @media (max-width: 480px) {
    width: calc(100vw - 32px);
    max-height: 250px;
  }
`;

const DropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: transparent;
  overscroll-behavior: contain; /* 防止滚动传播到父元素 */
  touch-action: none; /* 禁用默认的触摸行为 */
`;

const VoterItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;

  &:not(:last-child) {
    margin-bottom: 2px;
  }

  @media (max-width: 480px) {
    gap: 6px;
    padding: 4px 0;
  }
`;

const VoterItemAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  box-shadow: none;

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
  }
`;

const VoterItemName = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.text};

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const VoterDropdownTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondaryText};
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};

  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: 6px;
    padding-bottom: 6px;
  }
`;

const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const FullScreenImage = styled.img`
  max-width: 95%;
  max-height: 95%;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  z-index: 1001;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
`;

const CreationGrid = styled(Masonry)`
  display: flex;
  width: auto;
  margin: 32px -12px 0;

  .masonry-grid_column {
    padding: 0 12px;
    background-clip: padding-box;
    width: 25% !important; /* 强制每行四列 */

    @media (max-width: 1200px) {
      width: 33.333% !important;
    }

    @media (max-width: 900px) {
      width: 50% !important;
    }

    @media (max-width: 580px) {
      width: 100% !important;
    }

    > div {
      margin-bottom: 24px;
    }
  }

  @media (max-width: 1440px) {
    margin: 28px -10px 0;

    .masonry-grid_column {
      padding: 0 10px;

      > div {
        margin-bottom: 20px;
      }
    }
  }

  @media (max-width: 1024px) {
    margin: 24px -8px 0;

    .masonry-grid_column {
      padding: 0 8px;

      > div {
        margin-bottom: 16px;
      }
    }
  }

  @media (max-width: 768px) {
    margin: 20px -6px 0;

    .masonry-grid_column {
      padding: 0 6px;

      > div {
        margin-bottom: 12px;
      }
    }
  }

  @media (max-width: 480px) {
    margin: 16px -4px 0;

    .masonry-grid_column {
      padding: 0 4px;

      > div {
        margin-bottom: 8px;
      }
    }
  }
`;

const ActionButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 24px;
  margin-bottom: 0;
  position: relative;
  gap: 16px;
`;

const ActionButton = styled.button`
  background: var(--brand, #FF4B3F);
  width: 238px;
  height: 72px;
  border-radius: 200px;
  padding: 7px 24px;
  gap: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: HanaMinA;
  font-weight: 500;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  color: white;
  white-space: nowrap;

  @media (max-width: 1024px) {
    width: 220px;
    height: 64px;
    font-size: 28px;
  }

  @media (max-width: 768px) {
    width: 200px;
    height: 56px;
    font-size: 24px;
  }

  @media (max-width: 480px) {
    width: 180px;
    height: 48px;
    font-size: 20px;
    padding: 5px 16px;
  }

  &:hover {
    filter: brightness(0.85);
    box-shadow: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const DownloadAllButton = styled.button`
  color: var(--brand, #FF4B3F);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  padding: 8px 0;
  background: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  .download-icon {
    display: inline-flex;
    align-items: center;
  }
`;

const WinnerImagesWrapper = styled.div`
  width: 100%;
  overflow: visible;
  display: flex;
  justify-content: flex-end;
  position: relative;
`;

const WinnerImagesContainer = styled.div`
  display: flex;
  gap: 24px;
  width: auto;
  justify-content: flex-start;
  flex-wrap: nowrap;
  padding: 10px 0;
  overflow-x: auto;
  position: relative;

  ${props => props.$totalImages === 3 && `
    position: absolute;
    top: -20px;
    left: calc(100% - 3 * 371px - 2 * 24px);
    padding-right: 24px;
    box-sizing: border-box;
    width: auto;
  `}

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 1440px) {
    gap: 20px;
  }

  @media (max-width: 1024px) {
    justify-content: flex-start;
    margin-left: 0;
    gap: 16px;
    position: relative;
    top: auto;
    left: auto;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x mandatory;

    & > * {
      scroll-snap-align: start;
    }
  }

  @media (max-width: 768px) {
    gap: 12px;
    padding: 5px 0;
  }

  @media (max-width: 480px) {
    gap: 8px;
    padding: 3px 0;
  }
`;

const WinnerCard = styled.div`
  flex: 0 0 auto;
  max-width: ${props => {
    switch(props.$totalImages) {
      case 1:
        return props.$isVertical ? '474px' : '764px';
      case 2:
        return '381px';
      case 3:
        return '371px'; // 固定宽度371px
      case 4:
        return '269.75px';
      case 5:
        return '211.1999969482422px';
      default:
        return '211.1999969482422px';
    }
  }};
  width: ${props => {
    switch(props.$totalImages) {
      case 1:
        return props.$isVertical ? '474px' : '764px';
      case 2:
        return '381px';
      case 3:
        return '371px'; // 固定宽度371px
      case 4:
        return '269.75px';
      case 5:
        return '211.1999969482422px';
      default:
        return '211.1999969482422px';
    }
  }};
  margin-bottom: 0;
  background: ${({ theme }) => theme.cardBg};
  border-radius: 0;
  overflow: visible;
  transition: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 900px) {
    width: ${props => {
      switch(props.$totalImages) {
        case 1:
          return props.$isVertical ? '300px' : '400px';
        case 2:
          return '280px';
        case 3:
          return '260px'; // 保持与BattleDetail一致
        case 4:
          return '269.75px';
        case 5:
          return '240px';
        default:
          return '240px';
      }
    }};
    min-width: auto;
  }
`;

const WinnerTextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CreationWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.$totalImages === 4 ? '271.20416259765625px' : '100%'};
  overflow: visible;
`;

const WinnerImage = styled.img`
  width: auto;
  max-width: 380px;
  height: auto;
  max-height: 400px;
  border-radius: 0;
  object-fit: contain;
  object-position: center;
  background-color: transparent;

  @media (max-width: 1024px) {
    max-width: 280px;
  }

  @media (max-width: 768px) {
    max-width: 220px;
  }
`;

const Section = styled.div`
  margin-top: ${props => props.$hasThreeImages ? '-50px' : '40px'};
  padding-top: ${props => props.$hasThreeImages ? '0px' : '0'};
  padding-right: 0;
  padding-left: 0;
  position: relative;
  z-index: 1;
  overflow: visible;

  @media (max-width: 768px) {
    margin-top: 32px;
    padding-top: 0;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  z-index: 1002;
  opacity: 0.5;
  transition: opacity 0.3s ease;

  &.prev {
    left: 20px;
  }

  &.next {
    right: 20px;
  }

  &:hover {
    opacity: 1;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 10px;
  padding: 4px 8px;
  font-size: 14px;
  z-index: 1002;
`;

const BattleResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { fightInfo, fightStatus, userInfo } = useWebSocket();
  const [battleDetail, setBattleDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [winners, setWinners] = useState([]);
  const [creations, setCreations] = useState([]);
  const [accessAllowed, setAccessAllowed] = useState(true);
  const [selectedVoter, setSelectedVoter] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [showAccessError, setShowAccessError] = useState(false);
  const [expandedVoterLists, setExpandedVoterLists] = useState({});
  const [openVoterDropdown, setOpenVoterDropdown] = useState(null);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);
  const fullScreenWheelRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  const processImageUrl = (url, isAvatar = false) => {
    if (!url && isAvatar) {
      return '';
    }

    if (!url) return '';

    if (url.startsWith('data:')) {
      return url;
    }

    if (isAvatar && url.includes('https://static.battleverse.cn/userpic/')) {
      return url;
    }

    if (isAvatar && (url.startsWith('http://') || url.startsWith('https://'))) {
      return url;
    }

    if (isAvatar && /^\d+(\.\w+)?$/.test(url)) {
      return `https://static.battleverse.cn/userpic/${url}`;
    }

    if (isAvatar && (url.includes('avatars/') || url.startsWith('/avatars/'))) {
      const avatarName = url.split('avatars/')[1];
      return `https://static.battleverse.cn/userpic/${avatarName}`;
    }

    if (isAvatar && (url.includes('userpic/') || url.startsWith('/userpic/'))) {
      const avatarName = url.split('userpic/')[1];
      return `https://static.battleverse.cn/userpic/${avatarName}`;
    }

    if (isAvatar) {
      return `https://static.battleverse.cn/userpic/${url}`;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    if (url.startsWith('/')) {
      return `https://static.battleverse.cn${url}`;
    }

    return `https://static.battleverse.cn/${url}`;
  };

  useEffect(() => {
    console.log('WebSocket context:', { fightInfo, fightStatus, userInfo });
    console.log('User context:', user);
  }, [fightInfo, fightStatus, userInfo, user]);

  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setAccessAllowed(false);
        Message.error('请先登录');
        navigate('/');
        return;
      }

      setAccessAllowed(true);
    };

    checkAccess();

    return () => {
    };
  }, [navigate, user]);

  useEffect(() => {
    if (!accessAllowed) return;

    const fetchBattleResult = async () => {
      let loadingMessageId;
      try {
        setLoading(true);
        loadingMessageId = Message.loading('加载中...', 0);

        const response = await axios.post('//server.battleverse.cn/index/fight/item.do', {
          fightId: id
        }, {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        });

        if (!response.data.success) {
          throw new Error(response.data.msg || '获取对战结果失败');
        }

        const battleData = response.data.data;

        console.log('Battle data received:', battleData);
        console.log('Chinese theme name:', battleData.fightNameCn);

        if (!battleData) {
          throw new Error('对战数据不存在');
        }

        const battleDetail = {
          fightName: battleData.fightName,
          fightNameCn: battleData.fightNameCn,
          fightNameEn: battleData.fightNameEn,
          fightDesc: battleData.fightDesc,
          createTime: battleData.createTime,
          endTime: battleData.endTime,
          fightUserCount: battleData.fightUserCount
        };

        console.log('Battle detail being set:', battleDetail);

        setBattleDetail(battleDetail);

        if (battleData.winnerList && battleData.winnerList.length > 0) {
          const sortedWinners = battleData.winnerList.sort((a, b) => {
            const aVotes = a.voteNumber || (a.voteList ? a.voteList.length : 0);
            const bVotes = b.voteNumber || (b.voteList ? b.voteList.length : 0);
            return bVotes - aVotes;
          });

          setWinners(sortedWinners);
        } else {
          setWinners([]);
        }

        setCreations(battleData.creationList || []);

        setError(null);
      } catch (err) {
        console.error('获取对战结果错误:', err);
        const errorMessage = err.response?.data?.msg || err.message || '获取对战结果失败';
        setError(errorMessage);
        Message.error(errorMessage);

        if (errorMessage.includes('未登录')) {
          navigate('/');
        }
      } finally {
        setLoading(false);
        if (loadingMessageId) {
          Message.destroy(loadingMessageId);
        }
      }
    };

    if (id) {
      fetchBattleResult();
    }
  }, [id, accessAllowed, user, navigate]);

  const handleBack = () => {
    navigate('/');
  };

  useEffect(() => {
    const isAdminUser =
      user?.userType === 1 ||
      user?.type === 1 ||
      user?.role === 'admin' ||
      user?.isAdmin ||
      user?.admin;

    setIsAdmin(isAdminUser);
    console.log('是否管理员:', isAdminUser);
  }, [user]);

  const handleSetupNextBattle = () => {
    navigate('/battle-settings');
  };

  const handleJoinNextBattle = () => {
    navigate('/battle');
  };

  const hasNextBattle = fightStatus && fightStatus > 0;

  useEffect(() => {
    console.log('User object:', user);
    console.log('Is admin (computed):', isAdmin);
    console.log('Admin button render condition:', isAdmin);
    if (user) {
      console.log('User role:', user.role);
      console.log('User type:', user.userType);
    }
  }, [user, isAdmin]);

  const handleVoterClick = (voter, event) => {
    event.stopPropagation();

    if (selectedVoter && selectedVoter.userId === voter.userId) {
      setSelectedVoter(null);
      return;
    }

    const rect = event.target.getBoundingClientRect();
    const windowWidth = window.innerWidth;

    let x = rect.left + rect.width / 2;
    const y = rect.bottom + 10;

    const popupWidth = 220;

    if (x - (popupWidth / 2) < 10) {
      x = popupWidth / 2 + 10;
    } else if (x + (popupWidth / 2) > windowWidth - 10) {
      x = windowWidth - (popupWidth / 2) - 10;
    }

    setPopupPosition({ x, y });
    setSelectedVoter(voter);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setSelectedVoter(null);
    };

    const handleScroll = () => {
      // 如果下拉菜单打开，直接关闭
      if (openVoterDropdown) {
        setOpenVoterDropdown(null);
        setDropdownPosition(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleVoterList = (winnerId) => {
    setExpandedVoterLists(prev => ({
      ...prev,
      [winnerId]: !prev[winnerId]
    }));
  };

  const renderVoterList = (winner) => {
    if (!winner.voteList || winner.voteList.length === 0) return null;

    const isExpanded = !!expandedVoterLists[winner.id];
    const displayCount = 5;

    if (winner.voteList.length <= displayCount) {
      return (
        <VoterList>
          {winner.voteList.map((voter, idx) => (
            <VoterAvatar
              key={idx}
              src={processImageUrl(voter.userPic, true) || '/avatars/default.png'}
              alt={voter.userName}
              title={voter.userName}
              onClick={(e) => handleVoterClick(voter, e)}
            />
          ))}
        </VoterList>
      );
    } else {
      return (
        <>
          <VoterList>
            {winner.voteList.slice(0, displayCount).map((voter, idx) => (
              <VoterAvatar
                key={idx}
                src={processImageUrl(voter.userPic, true) || '/avatars/default.png'}
                alt={voter.userName}
                title={voter.userName}
                onClick={(e) => handleVoterClick(voter, e)}
              />
            ))}
            <MoreVotersIndicator
              onClick={() => toggleVoterList(winner.id)}
              title={isExpanded ? "收起" : `还有${winner.voteList.length - displayCount}人`}
            >
              {isExpanded ? "-" : `+${winner.voteList.length - displayCount}`}
            </MoreVotersIndicator>
          </VoterList>

          {isExpanded && (
            <ExtendedVoterList>
              {winner.voteList.slice(displayCount).map((voter, idx) => (
                <VoterAvatar
                  key={idx}
                  src={processImageUrl(voter.userPic, true) || '/avatars/default.png'}
                  alt={voter.userName}
                  title={voter.userName}
                  onClick={(e) => handleVoterClick(voter, e)}
                />
              ))}
            </ExtendedVoterList>
          )}
        </>
      );
    }
  };

  const toggleVoterDropdown = (creationId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (openVoterDropdown === creationId) {
      setOpenVoterDropdown(null);
      setDropdownPosition(null);
      return;
    }

    const triggerElement = event.currentTarget;
    if (triggerElement) {
      const rect = triggerElement.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      const dropdownHeight = 300;

      let position = {};

      if (windowWidth < 768) {
        position = {
          position: 'fixed',
          top: 'auto',
          bottom: 'auto',
          left: '50%',
          transform: 'translateX(-50%)',
          width: windowWidth < 480 ? 'calc(100vw - 32px)' : '200px'
        };

        if (rect.bottom + dropdownHeight > windowHeight - 20) {
          if (rect.top > windowHeight / 2) {
            position.bottom = (windowHeight - rect.top + 8) + 'px';
          } else {
            position.top = Math.max(windowHeight * 0.2, 50) + 'px';
            position.maxHeight = (windowHeight * 0.6) + 'px';
          }
        } else {
          position.top = (rect.bottom + 8) + 'px';
        }
      } else {
        if (rect.bottom + dropdownHeight > windowHeight * 0.7) {
          position = {
            position: 'fixed',
            width: '240px'
          };

          if (rect.top > windowHeight * 0.7) {
            position.bottom = (windowHeight - rect.top + 8) + 'px';
            position.left = rect.left + 'px';
          } else if (rect.top < dropdownHeight + 40) {
            position.top = Math.min(rect.bottom + 8, windowHeight * 0.2) + 'px';
            position.left = rect.left + 'px';
            position.maxHeight = (windowHeight * 0.7) + 'px';
          } else {
            position.bottom = (windowHeight - rect.top + 8) + 'px';
            position.left = rect.left + 'px';
          }

          const rightEdgeDistance = windowWidth - rect.right;
          if (rightEdgeDistance < 240) {
            position.left = 'auto';
            position.right = (windowWidth - rect.right) + 'px';
          }
        } else {
          position = {
            position: 'fixed',
            top: rect.bottom + 8 + 'px',
            left: rect.left + 'px'
          };

          const rightEdgeDistance = windowWidth - rect.right;
          if (rightEdgeDistance < 240) {
            position.left = 'auto';
            position.right = (windowWidth - rect.right) + 'px';
          }
        }
      }

      setDropdownPosition(position);
    }

    setOpenVoterDropdown(creationId);
  };

  // 创建一个单独的触摸移动处理函数，供下拉菜单使用
  const handleDropdownTouchMove = (e) => {
    // 如果滚动发生在下拉菜单内部，则阻止事件传播
    if (e.target.closest('.voter-dropdown')) {
      // 只阻止传播，不阻止默认行为，这样菜单内部可以滚动
      e.stopPropagation();
    } else {
      // 如果不是在菜单内部，阻止默认行为防止页面滚动
      e.preventDefault();
    }
  };

  // 控制body滚动锁定的effect
  useEffect(() => {
    if (openVoterDropdown) {
      // 存储原始的body样式
      const originalStyle = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        touchAction: document.body.style.touchAction
      };

      // 禁用body滚动
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'relative';
      document.body.style.touchAction = 'none';

      // 组件卸载或下拉菜单关闭时恢复原始样式
      return () => {
        document.body.style.overflow = originalStyle.overflow;
        document.body.style.position = originalStyle.position;
        document.body.style.touchAction = originalStyle.touchAction;
      };
    }
  }, [openVoterDropdown]);

  const renderDropdownContent = (creation) => {
    if (!creation) return null;

    const hasVotes = creation.voteList && creation.voteList.length > 0;

    return (
      <VoterDropdown className="voter-dropdown" onClick={(e) => e.stopPropagation()}>
        <VoterDropdownTitle>投票者列表</VoterDropdownTitle>
        {!hasVotes ? (
          <VoterItem>
            <span>暂无投票</span>
          </VoterItem>
        ) : (
          creation.voteList.map((voter, index) => (
            <VoterItem key={index}>
              <VoterItemAvatar
                src={processImageUrl(voter.userPic, true)}
                alt={voter.userName}
              />
              <VoterItemName>{voter.userName}</VoterItemName>
            </VoterItem>
          ))
        )}
      </VoterDropdown>
    );
  };

  const renderVoterDropdown = () => {
    if (!openVoterDropdown || !dropdownPosition) return null;

    const allCreations = getSortedCreations();
    const openCreation = allCreations.find(creation =>
      (creation.id === openVoterDropdown || creation.creationId === openVoterDropdown)
    );

    if (!openCreation) return null;

    return ReactDOM.createPortal(
      <DropdownOverlay
        onClick={() => {
          setOpenVoterDropdown(null);
          setDropdownPosition(null);
        }}
        onTouchMove={handleDropdownTouchMove}
      >
        <div
          style={{
            ...dropdownPosition,
            zIndex: 10000
          }}
          onClick={e => e.stopPropagation()}
          className="voter-dropdown-container"
        >
          {renderDropdownContent(openCreation)}
        </div>
      </DropdownOverlay>,
      document.body
    );
  };

  const handleImageClick = (imageUrl) => {
    const images = [];

    if (winners && winners.length > 0) {
      winners.forEach(winner => {
        if (winner.creationUrl) {
          images.push(processImageUrl(winner.creationUrl));
        }
      });
    }

    if (creations && creations.length > 0) {
      creations.forEach(creation => {
        if (creation.creationUrl) {
          images.push(processImageUrl(creation.creationUrl));
        }
      });
    }

    const uniqueImages = [...new Set(images)];
    setAllImages(uniqueImages);

    const index = uniqueImages.findIndex(img => img === imageUrl);
    setCurrentImageIndex(index >= 0 ? index : 0);

    setFullScreenImage(imageUrl);
  };

  const closeFullScreen = () => {
    setFullScreenImage(null);
  };

  const goToPrevImage = useCallback(() => {
    if (allImages.length <= 1) return;

    const newIndex = (currentImageIndex - 1 + allImages.length) % allImages.length;
    setCurrentImageIndex(newIndex);
    setFullScreenImage(allImages[newIndex]);
  }, [allImages, currentImageIndex]);

  const goToNextImage = useCallback(() => {
    if (allImages.length <= 1) return;

    const newIndex = (currentImageIndex + 1) % allImages.length;
    setCurrentImageIndex(newIndex);
    setFullScreenImage(allImages[newIndex]);
  }, [allImages, currentImageIndex]);

  const handleWheel = useCallback((e) => {
    if (!fullScreenImage) return;

    e.preventDefault();

    if (e.deltaY > 0) {
      goToNextImage();
    } else {
      goToPrevImage();
    }
  }, [fullScreenImage, goToNextImage, goToPrevImage]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (fullScreenImage) {
        if (e.key === 'Escape') {
          setFullScreenImage(null);
        } else if (e.key === 'ArrowRight') {
          goToNextImage();
        } else if (e.key === 'ArrowLeft') {
          goToPrevImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    const wheelTarget = fullScreenWheelRef.current;
    if (wheelTarget) {
      wheelTarget.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);

      if (wheelTarget) {
        wheelTarget.removeEventListener('wheel', handleWheel);
      }
    };
  }, [fullScreenImage, goToNextImage, goToPrevImage, handleWheel]);

  const handleDownloadAllImages = async () => {
    try {
      Message.loading('正在准备下载图片...', 0);

      const allImages = [];

      if (winners && winners.length > 0) {
        winners.forEach(winner => {
          if (winner.creationUrl) {
            allImages.push({
              url: winner.creationUrl,
              fileName: `winner_${winner.userName || 'unknown'}_${winner.id || Date.now()}.png`
            });
          }
        });
      }

      if (creations && creations.length > 0) {
        creations.forEach((creation, index) => {
          if (creation.creationUrl) {
            allImages.push({
              url: creation.creationUrl,
              fileName: `creation_${creation.userName || 'unknown'}_${creation.id || index}.png`
            });
          }
        });
      }

      if (allImages.length === 0) {
        Message.destroy();
        Message.error('没有找到可下载的图片');
        return;
      }

      const zip = new JSZip();
      const folder = zip.folder(`battle_images_${id || 'unknown'}`);

      Message.destroy();
      Message.loading(`准备下载 (0/${allImages.length})...`, 0);

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < allImages.length; i++) {
        const image = allImages[i];
        try {
          Message.destroy();
          Message.loading(`下载图片 (${i+1}/${allImages.length})...`, 0);

          const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(image.url)}`;

          const response = await fetch(proxyUrl);

          if (!response.ok) {
            throw new Error(`代理请求失败: ${response.status} ${response.statusText}`);
          }

          const blob = await response.blob();
          folder.file(image.fileName, blob);
          successCount++;
        } catch (error) {
          console.error(`下载图片失败: ${image.url}`, error);
          failCount++;

          try {
            const backupProxyUrl = `https://corsproxy.io/?${encodeURIComponent(image.url)}`;
            const backupResponse = await fetch(backupProxyUrl);

            if (!backupResponse.ok) {
              throw new Error(`备用代理请求失败: ${backupResponse.status}`);
            }

            const blob = await backupResponse.blob();
            folder.file(image.fileName, blob);
            successCount++;
            failCount--;
          } catch (backupError) {
            console.error(`备用代理也失败: ${image.url}`, backupError);

            folder.file(
              `${image.fileName}.failed.txt`,
              `无法下载此图片。原始链接:\n${image.url}`
            );
          }
        }
      }

      const readmeTxt =
        `# 对战图片 - ${battleDetail?.fightNameCn || '未知主题'}\n\n` +
        `总共${allImages.length}张图片，成功下载${successCount}张，${failCount}张下载失败。\n\n`;

      folder.file('README.txt', readmeTxt);

      Message.destroy();
      Message.loading('正在生成ZIP文件...', 0);

      const content = await zip.generateAsync({type: 'blob'});
      saveAs(content, `battle_images_${id || 'unknown'}.zip`);

      Message.destroy();

      if (successCount > 0) {
        if (failCount > 0) {
          Message.success(`已下载 ${successCount}/${allImages.length} 张图片，${failCount}张下载失败`);
        } else {
          Message.success(`已成功下载全部 ${allImages.length} 张图片！`);
        }
      } else {
        Message.error('无法下载图片，请尝试其他方法');
      }
    } catch (error) {
      console.error('打包图片时发生错误:', error);
      Message.destroy();
      Message.error('下载失败，请重试');
    }
  };

  const getSortedCreations = useCallback(() => {
    if (!winners || !creations) return [];

    const allCreations = [
      ...(winners || []),
      ...(Array.isArray(creations) ? creations : [])
    ];

    const uniqueCreations = [];
    const seenIds = new Set();

    allCreations.forEach(creation => {
      if (creation && creation.id && !seenIds.has(creation.id)) {
        seenIds.add(creation.id);
        uniqueCreations.push(creation);
      } else if (creation && creation.creationId && !seenIds.has(creation.creationId)) {
        seenIds.add(creation.creationId);
        uniqueCreations.push(creation);
      }
    });

    return uniqueCreations.sort((a, b) => {
      const aVotes = a.voteList ? a.voteList.length : (a.voteNumber || 0);
      const bVotes = b.voteList ? b.voteList.length : (b.voteNumber || 0);

      if (bVotes !== aVotes) {
        return bVotes - aVotes;
      }

      if (a.createTime && b.createTime) {
        return new Date(b.createTime) - new Date(a.createTime);
      }

      const aId = a.id || a.creationId || 0;
      const bId = b.id || b.creationId || 0;
      return aId - bId;
    });
  }, [winners, creations]);

  if (!user) {
    return (
      <PageWrapper>
        <Container>
          <div style={{
            textAlign: 'center',
            fontSize: '16px',
            color: ({ theme }) => theme.text,
            marginBottom: '20px'
          }}>
            请先登录后查看对战结果
          </div>
          <ActionButtonContainer>
            <ActionButton onClick={() => navigate('/')}>
              返回首页
            </ActionButton>
          </ActionButtonContainer>
        </Container>
      </PageWrapper>
    );
  }

  if (loading) {
    return (
      <PageWrapper>
        <Container>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
            fontSize: '16px',
            color: ({ theme }) => theme.secondaryText
          }}>
            加载中...
          </div>
        </Container>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <Container>
          <ActionButtonContainer>
            <ActionButton onClick={handleBack}>返回首页</ActionButton>
          </ActionButtonContainer>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      {fullScreenImage && (
        <FullScreenOverlay
          onClick={closeFullScreen}
          ref={fullScreenWheelRef}
        >
          <CloseButton onClick={closeFullScreen}>×</CloseButton>

          <NavigationButton
            className="prev"
            onClick={(e) => {
              e.stopPropagation();
              goToPrevImage();
            }}
          >
            &#10094;
          </NavigationButton>

          <FullScreenImage
            src={fullScreenImage}
            alt="全屏图片"
            onClick={(e) => e.stopPropagation()}
          />

          <NavigationButton
            className="next"
            onClick={(e) => {
              e.stopPropagation();
              goToNextImage();
            }}
          >
            &#10095;
          </NavigationButton>

          <ImageCounter>
            {currentImageIndex + 1} / {allImages.length}
          </ImageCounter>
        </FullScreenOverlay>
      )}

      {renderVoterDropdown()}

      <Container>
        <TopSection $hasThreeImages={winners.length === 3}>
          <MainContent>
            <Header>
              <ThemeSection>
                <ThemeTitle>主题</ThemeTitle>
                <ThemeContent>{battleDetail?.fightNameCn || '未知主题'}</ThemeContent>
              </ThemeSection>

              <WinnerTextSection>
                <WinnerSection>
                  <WinnerTitle>获胜者</WinnerTitle>
                  {winners.length > 0 && (
                    <WinnerInfo>
                      <WinnerUserRow>
                        {winners.map((winner, index) => (
                          <WinnerUser key={index}>
                            <WinnerAvatar
                              src={processImageUrl(winner.userPic, true) || '/avatars/default.png'}
                              alt={winner.userName}
                            />
                            <WinnerName>{winner.userName || '未知用户'}</WinnerName>
                          </WinnerUser>
                        ))}
                      </WinnerUserRow>
                    </WinnerInfo>
                  )}

                  {winners.length === 0 && (
                    <div style={{ fontSize: '14px', color: ({ theme }) => theme.secondaryText }}>
                      暂无获胜者
                    </div>
                  )}

                  {isAdmin ? (
                    <ActionButtonContainer>
                      <ActionButton onClick={handleSetupNextBattle}>
                        设置下轮
                      </ActionButton>
                      <DownloadAllButton
                        onClick={handleDownloadAllImages}
                      >
                        下载本局所有图片
                        <div className="download-icon">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M8 2v8M8 10l-3-3M8 10l3-3"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </DownloadAllButton>
                    </ActionButtonContainer>
                  ) : (
                    <ActionButtonContainer>
                      <ActionButton onClick={handleJoinNextBattle}>
                        参加下轮
                      </ActionButton>
                    </ActionButtonContainer>
                  )}
                </WinnerSection>
              </WinnerTextSection>
            </Header>
          </MainContent>

          {winners.length > 0 && (
            <WinnerImageSection>
              <WinnerImagesWrapper>
                <WinnerImagesContainer
                  $totalImages={winners.length}
                  style={{
                    overflowX: winners.length > 2 ? 'auto' : 'visible'
                  }}
                >
                  {winners.map((winner, index) => {

                    const maxWidth = (() => {
                      const count = winners.length;
                      if (count === 1) return '320px';
                      if (count === 2) return '280px';
                      if (count === 3) return '371px';
                      return '180px';
                    })();

                    const style = {
                      cursor: 'pointer',
                      objectFit: 'contain',
                      maxWidth: maxWidth,
                      height: 'auto'
                    };

                    if (winners.length === 3) {
                      style.height = '373px';
                    }

                    if (winners.length === 4) {
                      style.height = '271.20416259765625px';
                      style.maxWidth = '269.75px';
                    }

                    return (
                      <WinnerImage as={WorkThumbnail}
                        key={index}
                        src={processImageUrl(winner.creationUrl)}
                        alt={`${winner.userName}的作品`}
                        loading="lazy"
                        onClick={() => handleImageClick(processImageUrl(winner.creationUrl))}
                        style={style}
                      />
                    );
                  })}
                </WinnerImagesContainer>
              </WinnerImagesWrapper>
            </WinnerImageSection>
          )}
        </TopSection>

        {selectedVoter && (
          <VoterPopup $position={popupPosition} onClick={(e) => e.stopPropagation()}>
            <VoterPopupHeader>
              <VoterPopupAvatar
                src={processImageUrl(selectedVoter.userPic, true) || '/avatars/default.png'}
                alt={selectedVoter.userName}
              />
              <VoterPopupName>{selectedVoter.userName}</VoterPopupName>
            </VoterPopupHeader>
          </VoterPopup>
        )}

        <Section $hasThreeImages={winners.length === 3}>
          <CreationGrid
            breakpointCols={{
              default: 4,
              1200: 3,
              900: 2,
              580: 1
            }}
            className="masonry-grid"
            columnClassName="masonry-grid_column"
          >
            {getSortedCreations().map((creation, index) => (
              <CreationCard
                key={`creation-${creation.id || creation.creationId || index}`}
              >
                <CreationCardImage as={WorkThumbnail}
                  src={processImageUrl(creation.creationUrl)}
                  alt={`作品 ${index + 1}`}
                  loading="lazy"
                  onClick={() => handleImageClick(processImageUrl(creation.creationUrl))}
                  style={{ cursor: 'pointer' }}
                />
                <CreationInfo>
                  <CreationAuthor>
                    <AuthorAvatar
                      src={processImageUrl(creation.userPic, true) || '/avatars/default.png'}
                      alt={creation.userName}
                    />
                    <AuthorName>{creation.userName || '未知用户'}</AuthorName>
                  </CreationAuthor>
                  <CreationVotes
                    className="voter-count"
                    onClick={(e) => toggleVoterDropdown(creation.id || creation.creationId, e)}
                    data-id={creation.id || creation.creationId}
                  >
                    {creation.voteList?.length || creation.voteNumber || 0} 票
                  </CreationVotes>
                </CreationInfo>
              </CreationCard>
            ))}
          </CreationGrid>
        </Section>
      </Container>
    </PageWrapper>
  );
};

export default BattleResult;
